// Voice on the Square: small-group, peer-to-peer WebRTC audio between the
// people standing near you, each voice turned up or down by distance — the
// same hearing radii text uses, so voices fade exactly like bubbles do.
//
// Two ways in: `listen()` hears voices without a microphone (enough to follow
// the stage), `enable()` also turns your mic on. A connection is only opened
// when at least one side of the pair has a mic; a listener sends nothing and
// just receives. Blocked people (either direction) are never connected.
//
// Video is optional on top: `enableCamera()` adds a small camera track
// (240p, 15fps, bitrate-capped — it's a face in a bubble, not a stream),
// and the map draws each person's video in their token instead of their
// avatar. A connection carries video whenever either side has a camera on;
// the side without one asks to receive only.
//
// Signaling rides the page's CommunityChannel (offer / answer / ice / bye,
// addressed by the random per-page peer id the server hands out in `hello`);
// the audio goes browser to browser, through TURN when a direct path fails.
// A mesh: every pair within earshot holds its own connection, capped at
// MAX_PEERS nearest, which is plenty for a campfire conversation.
//
//   const voice = new SquareVoice({ iceServers, send, onChange })
//   voice.hello(peerId)               // from the server
//   voice.announce(frame)             // a `voice` frame: someone's mic on/off
//   voice.signal(frame)               // a `signal` frame addressed to us
//   voice.forget(userId)              // they left the map
//   voice.update(me, positionsById, hearing, { stage })  // ~4× a second
//   await voice.listen() / await voice.enable() / voice.stopTalking()
//   voice.disable() / voice.setMuted(bool) / voice.setExcluded(ids)
//   await voice.enableCamera() / voice.disableCamera() / voice.videoFor(id)
//   voice.level(userId)               // 0..1, how loudly they're speaking now

const MAX_PEERS = 6
const STAGE_PEERS = 16
const HANGUP_MARGIN = 80

export class SquareVoice {
    constructor({ iceServers, send, onChange }) {
        this.iceServers = iceServers || []
        this.send = send
        this.onChange = onChange || (() => {})
        this.peerId = null
        this.enabled = false // listening (with or without a mic)
        this.talking = false // mic on
        this.muted = false
        this.speakers = new Map() // userId -> peerId, for everyone in voice
        this.mics = new Set() // userIds whose mic is on
        this.cams = new Set() // userIds whose camera is on
        this.camera = false
        this.excluded = new Set() // blocked either way: never connect
        this.peers = new Map() // peerId -> { pc, userId, gain, analyser, audio, level, pending }
        this.localLevel = 0
        this.blocked = new Set()
        try { JSON.parse(window.localStorage.getItem("community-square:muted-people") || "[]").forEach((id) => this.blocked.add(id)) } catch (e) { /* noop */ }
    }

    hello(peerId) {
        this.peerId = peerId
        this.#announce()
    }

    // Hear voices without a microphone. Before the first click the audio
    // stays suspended and starts at that click (see #audio).
    async listen() {
        if (typeof RTCPeerConnection === "undefined") throw new Error("unsupported")
        await this.#audio()
        if (this.enabled) return true
        this.enabled = true
        this.#announce()
        this.onChange()
        return true
    }

    // Listen and talk: asks for the mic.
    async enable() {
        if (this.talking) return true
        if (!navigator.mediaDevices?.getUserMedia || typeof RTCPeerConnection === "undefined") throw new Error("unsupported")
        this.stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } })
        await this.#audio()
        const source = this.audioContext.createMediaStreamSource(this.stream)
        this.localAnalyser = this.audioContext.createAnalyser()
        this.localAnalyser.fftSize = 512
        source.connect(this.localAnalyser)
        // Connections opened while only listening carry no mic track; drop
        // them so they reopen with it.
        this.peers.forEach((_, peerId) => this.#hangUp(peerId, true))
        this.enabled = true
        this.talking = true
        this.setMuted(false)
        this.#announce()
        this.onChange()
        return true
    }

    // Mic off, keep listening.
    stopTalking() {
        if (!this.talking) return
        this.#releaseMic()
        this.peers.forEach((_, peerId) => this.#hangUp(peerId, true))
        this.#announce()
        this.onChange()
    }

    // Camera on: a small video track alongside (joining voice as a listener
    // first if need be). Asks for the camera.
    async enableCamera() {
        if (this.camera) return true
        if (!navigator.mediaDevices?.getUserMedia) throw new Error("unsupported")
        this.videoStream = await navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 240 }, height: { ideal: 240 }, frameRate: { ideal: 15, max: 20 }, facingMode: "user" } })
        if (!this.enabled) await this.listen()
        this.localVideo = document.createElement("video")
        this.localVideo.muted = true
        this.localVideo.playsInline = true
        this.localVideo.srcObject = this.videoStream
        this.localVideo.play().catch(() => {})
        this.camera = true
        this.peers.forEach((_, peerId) => this.#hangUp(peerId, true)) // reopen carrying video
        this.#announce()
        this.onChange()
        return true
    }

    disableCamera() {
        if (!this.camera) return
        this.#releaseCamera()
        this.peers.forEach((_, peerId) => this.#hangUp(peerId, true))
        this.#announce()
        this.onChange()
    }

    #releaseCamera() {
        this.camera = false
        this.videoStream?.getTracks().forEach((t) => t.stop())
        this.videoStream = null
        if (this.localVideo) this.localVideo.srcObject = null
        this.localVideo = null
    }

    // A playing <video> of someone ("self" for your own preview), or null.
    videoFor(userId) {
        if (userId === "self") return this.camera && this.localVideo?.readyState >= 2 ? this.localVideo : null
        const peerId = this.speakers.get(userId)
        const video = peerId ? this.peers.get(peerId)?.video : null
        return video && video.readyState >= 2 && video.videoWidth ? video : null
    }

    hasCamera(userId) {
        return this.cams.has(userId)
    }

    // Leave voice entirely.
    disable() {
        if (!this.enabled) return
        this.enabled = false
        this.#releaseMic()
        this.#releaseCamera()
        this.peers.forEach((_, peerId) => this.#hangUp(peerId, true))
        this.send("voice", { on: false })
        this.onChange()
    }

    setExcluded(ids) {
        this.excluded = new Set(ids)
        this.speakers.forEach((peerId, userId) => { if (this.excluded.has(userId)) this.#hangUp(peerId, true) })
        this.onChange()
    }

    #releaseMic() {
        this.talking = false
        this.stream?.getTracks().forEach((t) => t.stop())
        this.stream = null
        this.localAnalyser = null
    }

    // The browser keeps audio suspended until the page has been clicked or typed into. Voice starts
    // on arrival now (voice_pref.js), so don't wait on that: resume at the first gesture instead.
    async #audio() {
        this.audioContext = this.audioContext || new (window.AudioContext || window.webkitAudioContext)()
        if (this.audioContext.state !== "suspended") return
        this.audioContext.resume().catch(() => {})
        if (this.resumeOnGesture) return
        this.resumeOnGesture = () => {
            this.audioContext?.resume().catch(() => {})
            if (this.audioContext?.state === "running") ["pointerdown", "keydown", "touchend"].forEach((ev) => window.removeEventListener(ev, this.resumeOnGesture, true))
        }
        ;["pointerdown", "keydown", "touchend"].forEach((ev) => window.addEventListener(ev, this.resumeOnGesture, true))
    }

    #announce() {
        if (this.enabled && this.peerId) this.send("voice", { on: true, mic: this.talking, cam: this.camera })
    }

    setMuted(muted) {
        this.muted = muted
        this.stream?.getAudioTracks().forEach((t) => { t.enabled = !muted })
        this.onChange()
    }

    // Mute one person for yourself only; remembered on this device.
    toggleBlocked(userId) {
        if (this.blocked.has(userId)) this.blocked.delete(userId)
        else this.blocked.add(userId)
        try { window.localStorage.setItem("community-square:muted-people", JSON.stringify([...this.blocked])) } catch (e) { /* noop */ }
        this.onChange()
    }

    // Is their mic on? (Listeners are in voice too, but say nothing.)
    isSpeaker(userId) {
        return this.mics.has(userId)
    }

    isListening(userId) {
        return this.speakers.has(userId) && !this.mics.has(userId)
    }

    announce(frame) {
        if (!frame.peer || frame.peer === this.peerId) return
        if (frame.on) {
            const before = this.speakers.get(frame.id)
            const had = [this.mics.has(frame.id), this.cams.has(frame.id)].join()
            this.speakers.set(frame.id, frame.peer)
            if (frame.mic === false) this.mics.delete(frame.id)
            else this.mics.add(frame.id)
            if (frame.cam === true) this.cams.add(frame.id)
            else this.cams.delete(frame.id)
            // their mic or camera changed: reopen so the connection carries (or drops) it
            if (before === frame.peer && had !== [this.mics.has(frame.id), this.cams.has(frame.id)].join()) this.#hangUp(frame.peer, false)
        } else {
            this.speakers.delete(frame.id)
            this.mics.delete(frame.id)
            this.cams.delete(frame.id)
            this.#hangUp(frame.peer, false)
        }
        this.onChange()
    }

    forget(userId, peerId) {
        const peer = peerId || this.speakers.get(userId)
        this.speakers.delete(userId)
        this.mics.delete(userId)
        this.cams.delete(userId)
        if (peer) this.#hangUp(peer, false)
        this.onChange()
    }

    // Re-announce ourselves, e.g. after someone new arrives, so they learn
    // our mic is on without the server keeping voice state.
    reannounce() {
        this.#announce()
    }

    // Open connections to speakers within earshot (nearest first), close the
    // ones that walked away, and set every voice's volume by distance.
    // `stage` is the id of someone on the stage: they are heard everywhere.
    update(me, positions, hearing, { stage = null, meOnStage = false } = {}) {
        if (!this.enabled || !this.peerId) return
        const falloff = (d) => {
            if (d <= hearing.full) return 1
            if (d >= hearing.zero) return 0
            const t = (d - hearing.full) / (hearing.zero - hearing.full)
            return 1 - t * t * (3 - 2 * t)
        }
        const candidates = []
        this.speakers.forEach((peerId, userId) => {
            const p = positions.get(userId)
            if (!p || this.excluded.has(userId)) return
            // two listeners have nothing to say (or show) each other
            if (!this.talking && !this.camera && !this.mics.has(userId) && !this.cams.has(userId)) return
            const d = Math.hypot(p.x - me.x, p.y - me.y)
            candidates.push({ peerId, userId, d, onStage: userId === stage })
        })
        candidates.sort((a, b) => (a.onStage ? -1 : 0) - (b.onStage ? -1 : 0) || a.d - b.d)
        // On the stage you reach everyone with voice on, near or far (up to
        // STAGE_PEERS); otherwise only the nearest few within earshot.
        const wanted = new Set(meOnStage && this.talking
            ? candidates.slice(0, STAGE_PEERS).map((c) => c.peerId)
            : candidates.filter((c) => c.onStage || c.d < hearing.zero).slice(0, MAX_PEERS).map((c) => c.peerId))
        candidates.forEach((c) => {
            // Whoever has the lower peer id places the call; on the stage we
            // call everyone ourselves so nobody far away waits on us.
            if (wanted.has(c.peerId) && !this.peers.has(c.peerId) && ((meOnStage && this.talking) || this.peerId < c.peerId)) this.#call(c.peerId, c.userId)
            const peer = this.peers.get(c.peerId)
            if (!peer) return
            if (!(meOnStage && this.talking) && !c.onStage && c.d > hearing.zero + HANGUP_MARGIN) { this.#hangUp(c.peerId, true); return }
            const volume = this.blocked.has(c.userId) ? 0 : c.onStage ? 1 : falloff(c.d)
            if (peer.gain) peer.gain.gain.setTargetAtTime(volume, this.audioContext.currentTime, 0.12)
        })
        this.#measure()
    }

    level(userId) {
        if (userId === "self") return this.talking && !this.muted ? this.localLevel : 0
        const peerId = this.speakers.get(userId)
        return peerId ? this.peers.get(peerId)?.level || 0 : 0
    }

    // --- signaling ------------------------------------------------------------

    async signal(frame) {
        if (!this.enabled || !frame.from) return
        const { from, id: userId, kind, payload } = frame
        if (this.excluded.has(userId)) return
        try {
            if (kind === "bye") { this.#hangUp(from, false); return }
            let peer = this.peers.get(from)
            if (kind === "offer") {
                // Both sides called at once (someone on the stage): the lower
                // peer id's offer wins, the other side drops its own.
                if (peer && this.peerId < from && peer.pc.signalingState === "have-local-offer") return
                if (peer) this.#hangUp(from, false)
                peer = this.#connection(from, userId, false)
                await peer.pc.setRemoteDescription(payload)
                // add the mic and camera after the offer, so they ride the
                // transceivers the offer set up (a listener's offer asks to
                // receive only)
                this.#localTracks().forEach(([track, stream]) => peer.pc.addTrack(track, stream))
                await this.#flush(peer)
                const answer = await peer.pc.createAnswer()
                await peer.pc.setLocalDescription(answer)
                this.#capVideo(peer.pc)
                this.send("signal", { to: from, kind: "answer", payload: peer.pc.localDescription.toJSON() })
            } else if (kind === "answer" && peer) {
                await peer.pc.setRemoteDescription(payload)
                await this.#flush(peer)
            } else if (kind === "ice" && peer) {
                if (peer.pc.remoteDescription) await peer.pc.addIceCandidate(payload)
                else peer.pending.push(payload)
            }
        } catch (e) {
            this.#hangUp(from, true)
        }
    }

    async #call(peerId, userId) {
        const peer = this.#connection(peerId, userId, true)
        try {
            const offer = await peer.pc.createOffer()
            await peer.pc.setLocalDescription(offer)
            this.#capVideo(peer.pc)
            this.send("signal", { to: peerId, kind: "offer", payload: peer.pc.localDescription.toJSON() })
        } catch (e) {
            this.#hangUp(peerId, true)
        }
    }

    #connection(peerId, userId, offering) {
        const pc = new RTCPeerConnection({ iceServers: this.iceServers })
        const peer = { pc, userId, gain: null, analyser: null, audio: null, video: null, level: 0, pending: [] }
        this.peers.set(peerId, peer)
        if (offering) {
            this.#localTracks().forEach(([track, stream]) => pc.addTrack(track, stream))
            // whatever we don't send, we still offer to receive
            if (!this.stream) pc.addTransceiver("audio", { direction: "recvonly" })
            if (!this.videoStream) pc.addTransceiver("video", { direction: "recvonly" })
        }
        pc.onicecandidate = (e) => { if (e.candidate) this.send("signal", { to: peerId, kind: "ice", payload: e.candidate.toJSON() }) }
        pc.onconnectionstatechange = () => { if (["failed", "closed"].includes(pc.connectionState)) this.#hangUp(peerId, false) }
        pc.ontrack = (e) => {
            if (e.track.kind === "video") {
                peer.video = document.createElement("video")
                peer.video.muted = true
                peer.video.playsInline = true
                peer.video.srcObject = new MediaStream([e.track])
                peer.video.play().catch(() => {})
                this.onChange()
                return
            }
            const stream = new MediaStream([e.track])
            if (peer.gain) return
            // Chrome only feeds a remote WebRTC stream into Web Audio once it
            // is also attached to a (muted) media element.
            peer.audio = new Audio()
            peer.audio.muted = true
            peer.audio.srcObject = stream
            peer.audio.play().catch(() => {})
            const source = this.audioContext.createMediaStreamSource(stream)
            peer.gain = this.audioContext.createGain()
            peer.gain.gain.value = 0
            peer.analyser = this.audioContext.createAnalyser()
            peer.analyser.fftSize = 512
            source.connect(peer.analyser)
            source.connect(peer.gain).connect(this.audioContext.destination)
            this.onChange()
        }
        return peer
    }

    async #flush(peer) {
        const queued = peer.pending.splice(0)
        for (const candidate of queued) await peer.pc.addIceCandidate(candidate).catch(() => {})
    }

    #hangUp(peerId, tell) {
        const peer = this.peers.get(peerId)
        if (!peer) return
        this.peers.delete(peerId)
        try { peer.pc.close() } catch (e) { /* noop */ }
        try { peer.gain?.disconnect() } catch (e) { /* noop */ }
        if (peer.audio) peer.audio.srcObject = null
        if (peer.video) peer.video.srcObject = null
        if (tell) this.send("signal", { to: peerId, kind: "bye", payload: {} })
    }

    #localTracks() {
        const tracks = []
        if (this.stream) this.stream.getTracks().forEach((t) => tracks.push([t, this.stream]))
        if (this.videoStream) this.videoStream.getTracks().forEach((t) => tracks.push([t, this.videoStream]))
        return tracks
    }

    // A face in a bubble needs very little: cap each video send.
    #capVideo(pc) {
        pc.getSenders().filter((sender) => sender.track?.kind === "video").forEach((sender) => {
            try {
                const params = sender.getParameters()
                if (!params.encodings || !params.encodings.length) params.encodings = [{}]
                params.encodings[0].maxBitrate = 180_000
                sender.setParameters(params).catch(() => {})
            } catch (e) { /* not every browser lets us */ }
        })
    }

    // How loud each voice (and our own mic) is right now, for the speaking
    // rings on the map.
    #measure() {
        const rms = (analyser) => {
            if (!analyser) return 0
            const data = new Uint8Array(analyser.fftSize)
            analyser.getByteTimeDomainData(data)
            let sum = 0
            for (let i = 0; i < data.length; i++) { const v = (data[i] - 128) / 128; sum += v * v }
            return Math.min(1, Math.sqrt(sum / data.length) * 6)
        }
        this.localLevel = this.localLevel * 0.6 + rms(this.localAnalyser) * 0.4
        this.peers.forEach((peer) => { peer.level = peer.level * 0.6 + rms(peer.analyser) * 0.4 })
    }
}
